export type BakimDurum = 'yesil' | 'sari' | 'turuncu' | 'kirmizi';

export interface BakimBilgi {
  ayFarki: number;
  gunFarki: number;
  durum: BakimDurum;
  etiket: string;
  aciklama: string;
  progressYuzde: number;
  altiAyTarihi: string;
  dokuzAyTarihi: string;
  onikiAyTarihi: string;
}

export interface BakimEsikler {
  sariAy: number;
  turuncuAy: number;
  kirmiziAy: number;
}

const VARSAYILAN_ESIKLER: BakimEsikler = {
  sariAy: 6,
  turuncuAy: 9,
  kirmiziAy: 12,
};

/**
 * Son bakım tarihine göre 4 kademeli durum hesaplar.
 * 0-6 ay    → YEŞİL  (Bakım Süresi Uygun)
 * 6-9 ay    → SARI   (Bakım Zamanı Geldi)
 * 9-12 ay   → TURUNCU (Bakım Gecikiyor)
 * 12+ ay    → KIRMIZI (Kritik Bakım Gecikmesi)
 */
export function bakimHesapla(sonBakimTarihi: string, esikler?: BakimEsikler): BakimBilgi {
  const { sariAy, turuncuAy, kirmiziAy } = esikler || VARSAYILAN_ESIKLER;
  const now = new Date();
  const bakim = new Date(sonBakimTarihi);

  // Hesapla: son_bakim_tarihi + X months kullanarak gerçek takvim ayı hesabı
  const altiAyTarihi = new Date(bakim);
  altiAyTarihi.setMonth(altiAyTarihi.getMonth() + sariAy);

  const dokuzAyTarihi = new Date(bakim);
  dokuzAyTarihi.setMonth(dokuzAyTarihi.getMonth() + turuncuAy);

  const onikiAyTarihi = new Date(bakim);
  onikiAyTarihi.setMonth(onikiAyTarihi.getMonth() + kirmiziAy);

  const gunFarki = Math.floor((now.getTime() - bakim.getTime()) / (1000 * 60 * 60 * 24));
  const ayFarkiTahmini = gunFarki / 30;

  let durum: BakimDurum;
  let etiket: string;
  let aciklama: string;

  if (now < altiAyTarihi) {
    durum = 'yesil';
    etiket = 'Bakım Süresi Uygun';
    const kalanGun = Math.ceil((altiAyTarihi.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    aciklama = `${kalanGun} gün sonra bakım zamanı`;
  } else if (now < dokuzAyTarihi) {
    durum = 'sari';
    etiket = 'Bakım Zamanı Geldi';
    const gecenGun = Math.floor((now.getTime() - altiAyTarihi.getTime()) / (1000 * 60 * 60 * 24));
    aciklama = `Bakım zamanı ${gecenGun} gün önce geldi`;
  } else if (now < onikiAyTarihi) {
    durum = 'turuncu';
    etiket = 'Bakım Gecikiyor';
    const gecenGun = Math.floor((now.getTime() - dokuzAyTarihi.getTime()) / (1000 * 60 * 60 * 24));
    aciklama = `Bakım ${gecenGun} gündür gecikiyor`;
  } else {
    durum = 'kirmizi';
    etiket = 'Kritik Bakım Gecikmesi';
    const gecenGun = Math.floor((now.getTime() - onikiAyTarihi.getTime()) / (1000 * 60 * 60 * 24));
    aciklama = `Kritik gecikme: ${gecenGun} gün`;
  }

  const progressYuzde = Math.min(100, Math.round((ayFarkiTahmini / kirmiziAy) * 100));

  return {
    ayFarki: ayFarkiTahmini,
    gunFarki,
    durum,
    etiket,
    aciklama,
    progressYuzde,
    altiAyTarihi: altiAyTarihi.toISOString().split('T')[0],
    dokuzAyTarihi: dokuzAyTarihi.toISOString().split('T')[0],
    onikiAyTarihi: onikiAyTarihi.toISOString().split('T')[0],
  };
}

/**
 * Telefon numarasını normalize eder.
 * Giriş: 0532 123 45 67 → DB: 05321234567
 * Giriş: 905321234567 → DB: 05321234567
 */
export function telefonNormalize(tel: string): string {
  let temiz = tel.replace(/[\s\-\(\)\.]/g, '');
  if (temiz.startsWith('90') && temiz.length === 12) {
    temiz = '0' + temiz.slice(2);
  }
  return temiz;
}

/**
 * Telefonu okunabilir formata çevirir: 0532 123 45 67
 */
export function telefonGoster(tel: string): string {
  const temiz = tel.replace(/[\s\-\(\)\.]/g, '');
  if (temiz.startsWith('0') && temiz.length === 11) {
    return `${temiz.slice(0, 4)} ${temiz.slice(4, 7)} ${temiz.slice(7, 9)} ${temiz.slice(9, 11)}`;
  }
  return tel;
}

/**
 * WhatsApp linki için telefonu dönüştürür: 905321234567
 */
export function telefonWhatsApp(tel: string): string {
  const temiz = tel.replace(/[\s\-\(\)\.]/g, '');
  if (temiz.startsWith('0')) return '90' + temiz.slice(1);
  if (temiz.startsWith('90')) return temiz;
  return '90' + temiz;
}

/**
 * Son bakım tarihine göre sadece durum döndürür (yesil/sari/turuncu/kirmizi/yok).
 * Müşteri listesi gibi hızlı durum kontrolleri için.
 */
export function hesaplaBakimDurumu(sonBakimTarihi: string | null, esikler?: BakimEsikler): BakimDurum | 'yok' {
  if (!sonBakimTarihi) return 'yok';
  const son = new Date(sonBakimTarihi);
  const { sariAy, turuncuAy, kirmiziAy } = esikler || VARSAYILAN_ESIKLER;
  const simdi = new Date();

  const sariTarih = new Date(son);
  sariTarih.setMonth(sariTarih.getMonth() + sariAy);
  const turuncuTarih = new Date(son);
  turuncuTarih.setMonth(turuncuTarih.getMonth() + turuncuAy);
  const kirmiziTarih = new Date(son);
  kirmiziTarih.setMonth(kirmiziTarih.getMonth() + kirmiziAy);

  if (simdi < sariTarih) return 'yesil';
  if (simdi < turuncuTarih) return 'sari';
  if (simdi < kirmiziTarih) return 'turuncu';
  return 'kirmizi';
}